import { motion } from 'framer-motion'
import { PieChart as PieChartIcon, Target, TrendingUp } from 'lucide-react'

import { DashboardDonut, DashboardDonutLegend } from '@/components/dashboard/DashboardDonut'
import { ProgressRing } from '@/components/dashboard/ProgressRing'
import { useCommandDashboard } from '@/contexts/CommandDashboardContext'
import { guestMixChartData } from '@/lib/dashboard/dashboardChartData'
import { parseDashboardMoney } from '@/lib/dashboard/commandMetrics'

export function RevenueForecast() {
  const { data } = useCommandDashboard()
  const forecast = data?.revenue_forecast ?? {}
  const current = parseDashboardMoney(forecast.current)
  const projected = parseDashboardMoney(forecast.forecast)
  const target = parseDashboardMoney(forecast.target)
  const likelihood = Number(forecast.likelihood_pct ?? 0)
  const forecastPct = target > 0 ? Math.round((projected / target) * 100) : 0
  const guestData = guestMixChartData(data?.snapshot)

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-surface flex h-full flex-col bg-gradient-to-br from-card via-card to-primary/[0.05] p-4 lg:p-5"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-700">
            <Target className="h-4 w-4" strokeWidth={2.25} />
          </div>
          <h2 className="text-sm font-bold tracking-tight">End-of-day forecast</h2>
        </div>
        <TrendingUp className="h-4 w-4 text-emerald-600" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-muted/40 px-2 py-2.5 shadow-inner">
          <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Now</p>
          <p className="mt-0.5 text-sm font-bold tabular-nums">{(current / 1000).toFixed(1)}k</p>
        </div>
        <div className="rounded-xl bg-primary/10 px-2 py-2.5 shadow-sm">
          <p className="text-[9px] font-semibold uppercase tracking-wide text-primary">Forecast</p>
          <p className="mt-0.5 text-sm font-bold tabular-nums text-primary">
            {(projected / 1000).toFixed(1)}k
          </p>
        </div>
        <div className="rounded-xl bg-gold/10 px-2 py-2.5 shadow-inner">
          <p className="text-[9px] font-semibold uppercase tracking-wide text-[hsl(31_45%_42%)]">Target</p>
          <p className="mt-0.5 text-sm font-bold tabular-nums">{(target / 1000).toFixed(0)}k</p>
        </div>
      </div>

      <div className="mt-5 flex flex-1 flex-col items-center justify-center">
        <ProgressRing value={likelihood} size={96} stroke={6}>
          <span className="text-lg font-bold text-primary">{likelihood}%</span>
        </ProgressRing>
        <p className="mt-3 text-center text-xs font-semibold text-emerald-700">
          Likely to hit today&apos;s target
        </p>
        <p className="mt-1 text-center text-[11px] text-muted-foreground">
          {projected >= target ? 'Ahead of' : 'Behind'} target by AED{' '}
          {Math.abs(projected - target).toLocaleString()} ({forecastPct}%)
        </p>
      </div>

      {guestData.length > 0 ? (
        <div className="mt-4 border-t border-border/60 pt-4">
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <PieChartIcon className="h-3.5 w-3.5" />
            Guest split
          </div>
          <DashboardDonut data={guestData} centerLabel="Guests" height={120} />
          <DashboardDonutLegend data={guestData} />
        </div>
      ) : null}
    </motion.section>
  )
}
